import React, { useEffect, useState } from 'react';
import { adminAPI } from '../config/apiService';

// SIP admin "Announce Winners" view.
// - Current calendar month is the ONLY editable month (per user spec).
// - Each Site gets a card with 5 (name, prize) input pairs; blanks are skipped
//   server-side, so under-5 winners is fine.
// - Per-site Save and Print buttons.
// - Below each card: collapsible "Past months" panel sourced from
//   SIP_Admin_Get_Winners_History.
// SIP sites only (CW Sacramento, CW Valencia 1, CW Valencia 2). The proc
// also filters by Sites.SIPSiteCode server-side; this is belt-and-braces so
// the form never shows Swag-only sites even before §25/§21 are installed.
const SIP_SITE_IDS = [3, 5, 6];

// Display-name overrides. The Valencia 1 announcement covers both Valencia
// plants, so it shows as "CW Valencia 1 & 2" here and on the printed sheet
// (the §24 proc applies the same override server-side).
const SITE_DISPLAY = { 5: 'CW Valencia 1 & 2' };

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'];

function WinnersManager() {
    const now = new Date();
    // Month/year are selectable — sometimes winners are entered for a prior
    // (or different) month than today's.
    const [year, setYear] = useState(now.getFullYear());
    const [month, setMonth] = useState(now.getMonth() + 1);
    const monthName = MONTH_NAMES[month - 1];
    const yearChoices = [now.getFullYear() - 1, now.getFullYear(), now.getFullYear() + 1];

    const [groups, setGroups] = useState([]); // [{ siteID, site, slots: [row1..row5] }]
    const [edits, setEdits] = useState({});   // { siteID: [{displayOrder,winnerName,prizeAmount}] }
    const [history, setHistory] = useState({}); // { siteID: { open, loading, rows } }
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState({});     // { siteID: 'saving'|'printing' }
    const [msg, setMsg] = useState('');
    const [msgKind, setMsgKind] = useState('info');

    const flash = (text, kind = 'info') => { setMsg(text); setMsgKind(kind); };

    const reload = async () => {
        setLoading(true);
        try {
            const res = await adminAPI.getWinners(year, month);
            const rows = (Array.isArray(res.data) ? res.data : [])
                .filter((r) => SIP_SITE_IDS.includes(r.siteID));
            // Group server rows by site, keep slot order.
            const map = new Map();
            for (const r of rows) {
                if (!map.has(r.siteID)) {
                    map.set(r.siteID, { siteID: r.siteID, site: SITE_DISPLAY[r.siteID] || r.site, slots: [] });
                }
                map.get(r.siteID).slots.push(r);
            }
            const list = [...map.values()].sort((a, b) => a.site.localeCompare(b.site));
            setGroups(list);
            // Seed edits from server values so the inputs are controlled.
            const seed = {};
            for (const g of list) {
                seed[g.siteID] = g.slots.map((s) => ({
                    displayOrder: s.displayOrder,
                    winnerName: s.winnerName ?? '',
                    prizeAmount: s.prizeAmount == null ? '' : String(s.prizeAmount)
                }));
            }
            setEdits(seed);
        } catch {
            flash('Failed to load winners.', 'error');
        } finally { setLoading(false); }
    };

    // Reload whenever the selected period changes.
    useEffect(() => { reload(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [year, month]);

    const handleEdit = (siteID, displayOrder, field, value) => {
        setEdits((prev) => {
            const next = { ...prev };
            next[siteID] = (prev[siteID] || []).map((row) =>
                row.displayOrder === displayOrder ? { ...row, [field]: value } : row
            );
            return next;
        });
    };

    const siteHasAnyWinner = (siteID) =>
        (edits[siteID] || []).some((r) => r.winnerName.trim() !== '');

    const saveSite = async (siteID) => {
        const rows = (edits[siteID] || [])
            .map((r) => ({
                DisplayOrder: r.displayOrder,
                WinnerName: r.winnerName.trim(),
                PrizeAmount: r.prizeAmount === '' ? 0 : Number(r.prizeAmount)
            }))
            .filter((r) => r.WinnerName !== '');
        // Validate prize amounts.
        if (rows.some((r) => Number.isNaN(r.PrizeAmount) || r.PrizeAmount < 0)) {
            flash('Prize amounts must be 0 or greater.', 'error');
            return;
        }
        setBusy((p) => ({ ...p, [siteID]: 'saving' })); setMsg('');
        try {
            await adminAPI.saveSiteWinners({ year, month, siteID, winners: rows });
            await reload();
            // Re-open history if it was open, so the panel doesn't snap shut.
            if (history[siteID]?.open) loadHistory(siteID);
            flash('Saved.', 'success');
        } catch (err) {
            flash(`Save failed: ${err?.response?.data || err?.message || 'unknown error'}`, 'error');
        } finally {
            setBusy((p) => { const { [siteID]: _, ...rest } = p; return rest; });
        }
    };

    const printSite = async (siteID) => {
        if (!siteHasAnyWinner(siteID)) {
            flash('Add at least one winner before printing.', 'error');
            return;
        }
        setBusy((p) => ({ ...p, [siteID]: 'printing' })); setMsg('');
        try {
            const res = await adminAPI.printWinnersAnnouncement(siteID, year, month);
            const url = URL.createObjectURL(res.data);
            const a = document.createElement('a');
            a.href = url; a.target = '_blank'; a.rel = 'noopener';
            document.body.appendChild(a); a.click(); document.body.removeChild(a);
            setTimeout(() => URL.revokeObjectURL(url), 60_000);
            flash('Announcement opened.', 'success');
        } catch (err) {
            let detail = err?.message || 'unknown error';
            try {
                const data = err?.response?.data;
                if (data instanceof Blob) detail = (await data.text()).slice(0, 500) || detail;
            } catch { /* ignore */ }
            flash(`Print failed: ${detail}`, 'error');
        } finally {
            setBusy((p) => { const { [siteID]: _, ...rest } = p; return rest; });
        }
    };

    const loadHistory = async (siteID) => {
        setHistory((prev) => ({ ...prev, [siteID]: { ...(prev[siteID] || {}), open: true, loading: true } }));
        try {
            const res = await adminAPI.getWinnersHistory(siteID);
            setHistory((prev) => ({ ...prev, [siteID]: { open: true, loading: false, rows: res.data || [] } }));
        } catch {
            setHistory((prev) => ({ ...prev, [siteID]: { open: true, loading: false, rows: [], error: true } }));
        }
    };

    const toggleHistory = (siteID) => {
        const cur = history[siteID];
        if (cur?.open) {
            setHistory((prev) => ({ ...prev, [siteID]: { ...cur, open: false } }));
        } else {
            loadHistory(siteID);
        }
    };

    if (loading) return <div style={styles.muted}>Loading…</div>;

    return (
        <div>
            <div style={styles.card}>
                <div style={styles.eyebrow}>Announce Winners</div>
                <div style={styles.title}>{monthName} {year} Safety Incentive Winners</div>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center', margin: '8px 0' }}>
                    <label style={{ fontSize: 12, fontWeight: 700, color: '#374151' }}>Period:</label>
                    <select value={month} onChange={(e) => setMonth(Number(e.target.value))} style={styles.select}>
                        {MONTH_NAMES.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
                    </select>
                    <select value={year} onChange={(e) => setYear(Number(e.target.value))} style={styles.select}>
                        {yearChoices.map((y) => <option key={y} value={y}>{y}</option>)}
                    </select>
                </div>
                <div style={styles.muted}>
                    Past months show below each site as read-only history.
                    Up to 5 winners per site; blank rows are ignored.
                </div>
            </div>

            {msg && <Banner kind={msgKind}>{msg}</Banner>}

            {groups.length === 0 ? (
                <div style={{ ...styles.card, marginTop: 12 }}>
                    <div style={styles.muted}>No sites found.</div>
                </div>
            ) : (
                <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {groups.map((g) => (
                        <SiteCard
                            key={g.siteID}
                            group={g}
                            edits={edits[g.siteID] || []}
                            busy={busy[g.siteID]}
                            historyState={history[g.siteID]}
                            onEdit={handleEdit}
                            onSave={() => saveSite(g.siteID)}
                            onPrint={() => printSite(g.siteID)}
                            onToggleHistory={() => toggleHistory(g.siteID)}
                            canPrint={siteHasAnyWinner(g.siteID)}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}

function SiteCard({ group, edits, busy, historyState, onEdit, onSave, onPrint, onToggleHistory, canPrint }) {
    return (
        <div style={styles.card}>
            <div style={styles.siteHeader}>
                <div style={styles.siteName}>{group.site}</div>
                <div style={styles.actions}>
                    <button onClick={onSave} disabled={!!busy} style={btn('#2563eb', !!busy)}>
                        {busy === 'saving' ? 'Saving…' : 'Save'}
                    </button>
                    <button onClick={onPrint} disabled={!!busy || !canPrint} style={btn('#15803d', !!busy || !canPrint)}>
                        {busy === 'printing' ? 'Opening…' : 'Print Announcement'}
                    </button>
                </div>
            </div>

            <table style={styles.slotTable}>
                <thead style={styles.slotHead}>
                    <tr>
                        <th style={{ ...styles.th, width: 40 }}>#</th>
                        <th style={styles.th}>Winner</th>
                        <th style={{ ...styles.th, width: 140, textAlign: 'right' }}>Prize ($)</th>
                    </tr>
                </thead>
                <tbody>
                    {edits.map((row) => (
                        <tr key={row.displayOrder}>
                            <td style={{ ...styles.td, color: '#6b7280' }}>{row.displayOrder}</td>
                            <td style={styles.td}>
                                <input
                                    type="text"
                                    value={row.winnerName}
                                    onChange={(e) => onEdit(group.siteID, row.displayOrder, 'winnerName', e.target.value)}
                                    placeholder="Winner name"
                                    style={styles.input}
                                />
                            </td>
                            <td style={{ ...styles.td, textAlign: 'right' }}>
                                <input
                                    type="number" min="0" step="1"
                                    value={row.prizeAmount}
                                    onChange={(e) => onEdit(group.siteID, row.displayOrder, 'prizeAmount', e.target.value)}
                                    placeholder="0"
                                    style={{ ...styles.input, width: 110, textAlign: 'right' }}
                                />
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>

            <div style={{ marginTop: 10 }}>
                <button onClick={onToggleHistory} style={styles.historyToggle}>
                    {historyState?.open ? '▾' : '▸'} Past months
                </button>
                {historyState?.open && (
                    <HistoryPanel state={historyState} />
                )}
            </div>
        </div>
    );
}

function HistoryPanel({ state }) {
    if (state.loading) return <div style={{ ...styles.muted, padding: 8 }}>Loading history…</div>;
    if (state.error)   return <div style={{ ...styles.muted, padding: 8, color: '#dc2626' }}>Couldn't load history.</div>;
    const rows = state.rows || [];
    if (rows.length === 0) return <div style={{ ...styles.muted, padding: 8 }}>No past winners yet.</div>;

    // Group by Year/Month.
    const byMonth = groupHistoryByMonth(rows);

    return (
        <div style={{ marginTop: 8, padding: 8, background: '#f9fafb', borderRadius: 6 }}>
            {byMonth.map(({ key, label, items }) => (
                <div key={key} style={{ marginBottom: 12 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 4 }}>{label}</div>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                        <tbody>
                            {items.map((r) => (
                                <tr key={`${key}-${r.displayOrder}`}>
                                    <td style={{ ...styles.td, color: '#6b7280', width: 28 }}>{r.displayOrder}</td>
                                    <td style={styles.td}>{r.winnerName}</td>
                                    <td style={{ ...styles.td, textAlign: 'right', color: '#047857', fontWeight: 600 }}>
                                        {fmtMoney(r.prizeAmount)}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            ))}
        </div>
    );
}

// Tiny grouping helper — not a hook, just a function called during render.
// Rows are stable for the panel's lifetime so a useMemo wrapper isn't needed.
function groupHistoryByMonth(rows) {
    const map = new Map();
    for (const r of rows) {
        const key = `${r.year}-${String(r.month).padStart(2, '0')}`;
        if (!map.has(key)) {
            const label = new Date(r.year, r.month - 1, 1)
                .toLocaleString('en-US', { month: 'long', year: 'numeric' });
            map.set(key, { key, label, items: [] });
        }
        map.get(key).items.push(r);
    }
    return [...map.values()];
}

function fmtMoney(n) {
    if (n == null) return '';
    return n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
}

function Banner({ kind, children }) {
    const color = kind === 'error' ? '#dc2626' : kind === 'success' ? '#15803d' : '#374151';
    const bg = kind === 'error' ? '#fef2f2' : kind === 'success' ? '#f0fdf4' : '#f3f4f6';
    return <div style={{ margin: '12px 0', padding: '8px 12px', borderRadius: 4, background: bg, color, fontSize: 13 }}>{children}</div>;
}

const btn = (bg, disabled) => ({
    background: bg, color: 'white', border: 'none', padding: '8px 14px', borderRadius: 4,
    fontWeight: 600, fontSize: 13, cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.55 : 1
});

const styles = {
    card: { background: 'white', padding: 16, borderRadius: 8, border: '1px solid #e5e7eb' },
    muted: { color: '#6b7280', fontSize: 13 },
    eyebrow: { fontSize: 10, fontWeight: 700, color: '#9ca3af', letterSpacing: '0.1em', textTransform: 'uppercase' },
    title: { fontSize: 20, fontWeight: 800, color: '#111827', marginTop: 2, marginBottom: 4 },
    siteHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 8 },
    siteName: { fontSize: 16, fontWeight: 700, color: '#111827' },
    actions: { display: 'flex', gap: 8 },
    slotTable: { width: '100%', borderCollapse: 'collapse' },
    slotHead: { background: '#f9fafb', borderBottom: '1px solid #e5e7eb' },
    th: { textAlign: 'left', padding: '6px 10px', fontSize: 10, fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.04em' },
    td: { padding: '6px 10px', fontSize: 13, color: '#111827', borderBottom: '1px solid #f3f4f6' },
    input: { width: '100%', padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 13, boxSizing: 'border-box' },
    select: { padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 13, background: 'white' },
    historyToggle: { background: 'none', border: 'none', color: '#374151', fontSize: 12, fontWeight: 600, cursor: 'pointer', padding: 0 }
};

export default WinnersManager;
